import { Grid } from '@mui/material';
import { RoutalAvatarManager, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import lodash from 'lodash';
import { useEffect, useState } from 'react';

import { HeaderContent } from '../../../components/HeaderContent';
import { generateAttachmentName, MAX_AVATAR_IMAGE_SIZE, UPLOAD_TYPES } from '../../../helpers/Images';
import { intl } from '../../../helpers/IntGlobalProvider';
import useCreateOrganizationAvatar from '../../../hooks/organization/useCreateOrganizationAvatar';
import useRemoveOrganizationAvatar from '../../../hooks/organization/useDeleteOrganizationAvatar';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useUpdateOrganization } from '../../../hooks/organization/useUpdateOrganization';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';

type OrganizationProfileProps = {
  isDirtyState: (...args: any[]) => any;
};

export const OrganizationProfile = ({ isDirtyState }: OrganizationProfileProps) => {
  const { data: organization } = useRetrieveOrganization();
  const [label, setLabel] = useState<string>(``);
  const { run: uploadAvatar } = useCreateOrganizationAvatar({ organization: organization! });
  const { run: removeAvatar } = useRemoveOrganizationAvatar({ organization: organization! });
  const { isLoading, run: updateOrganization } = useUpdateOrganization();
  const [dirtyState, setDirtyState] = useState<boolean>(false);

  useEffect(() => {
    if (organization?.label) {
      setLabel(organization.label);
    }
  }, [organization]);

  useEffect(() => {
    if (organization) {
      if (!lodash.isEqual(label, organization.label)) {
        setDirtyState(true);
      } else {
        setDirtyState(false);
      }
    }
  }, [organization, label]);

  useEffect(() => {
    isDirtyState(dirtyState);
  }, [dirtyState, isDirtyState]);

  const handleEditOrganization = () => {
    updateOrganization({ label, organization: organization! });
  };

  if (!organization) return null;

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ height: `100%`, backgroundColor: `white`, overflowY: `auto` }}
    >
      <HeaderContent
        titleIntlId="settings.organization.profile.title"
        styles={{
          container: {
            padding: `16px 22px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            maxHeight: `120px`,
          },
        }}
      />

      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          padding: `16px 22px`,
          overflowY: `auto`,
          paddingBottom: `22px`,
        }}
      >
        <Grid item>
          <Text variant="body-large" intlId="settings.organization.profile.profile_picture" />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <RoutalAvatarManager
            id="organization_profile_image_upload"
            shape="round"
            avatarUrl={organization.avatar_url}
            fileName={generateAttachmentName(UPLOAD_TYPES.ORGANIZATION)}
            maxFileSize={MAX_AVATAR_IMAGE_SIZE}
            confirmText={intl.formatMessage({ id: `confirm` })}
            cancelText={intl.formatMessage({ id: `cancel` })}
            onRemoveAvatar={() => removeAvatar()}
            onAvatarUpload={(fileName: string, mimeType: string, file: Blob) => {
              uploadAvatar({
                fileName,
                mimeType,
                file,
              });
            }}
            getUploadAsBase64={getUploadAsBase64}
          />
        </Grid>

        <VSpacer medium />
        <VSpacer medium />

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <InputText
                id="label"
                label={
                  <Text
                    variant="body"
                    style={{ cursor: `default`, marginBottom: `8px` }}
                    intlId="settings.organization.profile.name"
                  />
                }
                fieldValue={label}
                onChange={(value: any) => setLabel(value)}
                inputStyles={{ width: `300px` }}
              />
            </Grid>
          </Grid>
        </Grid>

        <VSpacer medium />

        <Grid item style={{ position: `fixed`, bottom: `40px`, right: `40px` }}>
          <Button
            disabled={!dirtyState || isLoading}
            variant={dirtyState || isLoading ? `default` : `flat`}
            onClick={handleEditOrganization}
            style={{ maxWidth: `200px` }}
          >
            <Text
              variant="body"
              style={{
                cursor: dirtyState && !isLoading ? `pointer` : `default`,
                color: dirtyState || isLoading ? `white` : `inherit`,
              }}
              intlId="settings.buttonupdate"
            />
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
